import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  ISeriesApi,
  IChartApi,
} from 'lightweight-charts'

// Grouped signal data
export interface SignalMarkerData {
  time: number
  price: number
  side: 'bid' | 'ask'
  source: 'dom' | 'trade'
  /** DOM detection mode: 'precise' or 'aggregated' (DOM signals only) */
  domMode?: 'precise' | 'aggregated' | null
  maxQuantity: number
  count: number
  /** How many times the signal exceeds the threshold (1.0 = at threshold, 2.0 = 2x, capped at 4.0) */
  sizeRatio: number
}

// Coordinates for rendering
interface ViewPoint {
  x: number | null
  y: number | null
}

interface MarkerViewData {
  point: ViewPoint
  signal: SignalMarkerData
}

// Type for CanvasRenderingTarget2D from lightweight-charts
interface CanvasRenderingTarget2D {
  useMediaCoordinateSpace(callback: (scope: MediaCoordinatesRenderingScope) => void): void
}

interface MediaCoordinatesRenderingScope {
  context: CanvasRenderingContext2D
  mediaSize: { width: number; height: number }
}

// Renderer for drawing signal markers on the canvas
class SignalMarkersPaneRenderer {
  private _markers: MarkerViewData[]

  constructor(markers: MarkerViewData[]) {
    this._markers = markers
  }

  draw(target: CanvasRenderingTarget2D) {
    target.useMediaCoordinateSpace((scope) => {
      const ctx = scope.context

      for (const marker of this._markers) {
        const { point, signal } = marker
        if (point.x === null || point.y === null) continue

        const x = point.x
        const y = point.y
        const isAsk = signal.side === 'ask'
        const isTrade = signal.source === 'trade'
        const ratio = signal.sizeRatio // 1.0..4.0

        // Alpha scales with ratio: base 0.7 up to 1.0
        const alpha = Math.min(0.7 + (ratio - 1) * 0.1, 1.0)

        // Different color palettes for DOM vs Trade
        let fillColor: string
        let strokeColor: string
        if (isTrade) {
          // Trade: white (buy) / orange (sell)
          fillColor = isAsk
            ? `rgba(255, 152, 0, ${alpha * 0.8})`      // orange fill
            : `rgba(255, 255, 255, ${alpha * 0.8})`    // white fill
          strokeColor = isAsk
            ? `rgba(255, 152, 0, ${alpha})`             // orange stroke
            : `rgba(255, 255, 255, ${alpha})`           // white stroke
        } else {
          // DOM: red (ask) / green (bid) — classic colors
          fillColor = isAsk
            ? `rgba(239, 83, 80, ${alpha * 0.3})`
            : `rgba(38, 166, 154, ${alpha * 0.3})`
          strokeColor = isAsk
            ? `rgba(239, 83, 80, ${alpha})`
            : `rgba(38, 166, 154, ${alpha})`
        }

        // Base marker size 4px, scaled by ratio (capped at 4x)
        const baseSize = 4
        const markerSize = baseSize * ratio

        // Aggregated DOM levels are drawn hollow with a dashed outline so they
        // read differently from precise (solid) levels at a glance.
        const isAggregated = !isTrade && signal.domMode === 'aggregated'

        // Draw marker shape: diamond for DOM, circle for Trade
        ctx.beginPath()
        if (isTrade) {
          ctx.arc(x, y, markerSize, 0, Math.PI * 2)
        } else {
          ctx.moveTo(x, y - markerSize)
          ctx.lineTo(x + markerSize, y)
          ctx.lineTo(x, y + markerSize)
          ctx.lineTo(x - markerSize, y)
          ctx.closePath()
        }

        if (isAggregated) {
          // Hollow: skip the fill, use a dashed stroke
          ctx.setLineDash([3, 2])
        } else {
          ctx.fillStyle = fillColor
          ctx.fill()
        }
        ctx.strokeStyle = strokeColor
        ctx.lineWidth = ratio >= 2 ? 2 : 1
        ctx.stroke()
        ctx.setLineDash([])

        // Draw text label (no price — marker is already at the price level)
        const multiplier = signal.count > 1 ? ` x${signal.count}` : ''
        const text = `${Math.round(signal.maxQuantity)}${multiplier}`

        const isBold = ratio >= 2
        ctx.font = `${isBold ? 'bold ' : ''}11px monospace`
        ctx.textBaseline = 'middle'

        const textMetrics = ctx.measureText(text)
        const textWidth = textMetrics.width
        const textHeight = 14
        const padding = 4
        const gap = markerSize + 4
        const offsetX = isAsk ? gap : -gap - textWidth - padding * 2

        // Background for text
        const bgX = x + offsetX
        const bgY = y - textHeight / 2 - padding / 2

        ctx.fillStyle = 'rgba(19, 23, 34, 0.9)'
        ctx.beginPath()
        ctx.roundRect(bgX, bgY, textWidth + padding * 2, textHeight + padding, 3)
        ctx.fill()

        ctx.strokeStyle = strokeColor
        ctx.lineWidth = 1
        ctx.stroke()

        // Text
        ctx.fillStyle = strokeColor
        ctx.textAlign = 'left'
        ctx.fillText(text, bgX + padding, y + 1)
      }
    })
  }
}

// Pane view for signal markers
class SignalMarkersPaneView {
  private _source: SignalMarkersPrimitive
  private _markers: MarkerViewData[] = []

  constructor(source: SignalMarkersPrimitive) {
    this._source = source
  }

  update() {
    const series = this._source.series
    const timeScale = this._source.chart?.timeScale()
    const signals = this._source.signals

    if (!series || !timeScale) {
      this._markers = []
      return
    }

    this._markers = signals.map(signal => {
      const x = timeScale.timeToCoordinate(signal.time as Time)
      const y = series.priceToCoordinate(signal.price)

      return {
        point: { x, y },
        signal,
      }
    }).filter(m => m.point.x !== null && m.point.y !== null)
  }

  renderer() {
    return new SignalMarkersPaneRenderer(this._markers)
  }

  zOrder(): 'top' | 'bottom' | 'normal' {
    return 'top'
  }
}

// Main primitive class
export class SignalMarkersPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null
  private _series: ISeriesApi<'Candlestick'> | null = null
  private _signals: SignalMarkerData[] = []
  private _paneView: SignalMarkersPaneView
  private _requestUpdate?: () => void

  constructor() {
    this._paneView = new SignalMarkersPaneView(this)
  }

  get chart(): IChartApi | null {
    return this._chart
  }

  get series(): ISeriesApi<'Candlestick'> | null {
    return this._series
  }

  get signals(): SignalMarkerData[] {
    return this._signals
  }

  // Called when primitive is attached to a series
  attached(param: SeriesAttachedParameter<Time>) {
    this._chart = param.chart
    this._series = param.series as ISeriesApi<'Candlestick'>
    this._requestUpdate = param.requestUpdate
  }

  // Called when primitive is detached
  detached() {
    this._chart = null
    this._series = null
    this._requestUpdate = undefined
  }

  // Update signals and request chart redraw
  setSignals(signals: SignalMarkerData[]) {
    this._signals = signals
    if (this._requestUpdate) {
      this._requestUpdate()
    }
  }

  // Called before rendering to update view coordinates
  updateAllViews() {
    this._paneView.update()
  }

  // Return the pane views for rendering
  paneViews() {
    return [this._paneView]
  }

  // Provide autoscale info to include signal prices in the visible range
  autoscaleInfo() {
    if (this._signals.length === 0) return null

    const prices = this._signals.map(s => s.price)
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)

    return {
      priceRange: {
        minValue: minPrice,
        maxValue: maxPrice,
      },
    }
  }
}
